import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrgId } from '@/app/session';
import { SiteImage } from '@/components/ui';
import { cx } from '@/lib/cx';
import { locationQuery } from './api';

/** True once the element has come within `margin` of the viewport (and stays true). */
function useSeen<T extends Element>(margin = '240px') {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setSeen(true);
        io.disconnect();
      }
    }, { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, margin]);
  return { ref, seen };
}

/**
 * Satellite thumbnail for a list row or board card. Summaries carry no coordinates, so the
 * location record is loaded once the thumbnail nears the viewport (and shared with the side panel).
 */
export function SiteThumb({
  id,
  city,
  zoom = 16,
  width,
  height,
  className,
}: {
  id: string;
  city: string | null;
  zoom?: number;
  width: number | string;
  height: number | string;
  className?: string;
}) {
  const orgId = useOrgId();
  const { ref, seen } = useSeen<HTMLDivElement>();
  const coords = useQuery({
    ...locationQuery(orgId, id),
    enabled: seen,
    staleTime: 5 * 60_000,
    select: (l) => ({ lat: l.address.latitude, lon: l.address.longitude }),
  });
  return (
    <div ref={ref} className={cx('site-thumb', className)} style={{ width, height }} data-loading={coords.isPending || undefined}>
      {coords.isPending ? null : (
        <SiteImage lat={coords.data?.lat} lon={coords.data?.lon} zoom={zoom} width="100%" height="100%" label="" city={city} credit={false} pin={false} />
      )}
    </div>
  );
}
